<script setup>
import { watch } from 'vue'
import { useCurrencyInput } from 'vue-currency-input'

// inheritAttrs: false so that native event listeners (@blur, @change etc.) passed from
// the parent are forwarded to the inner <input> via v-bind="$attrs", not to the root div
defineOptions({ inheritAttrs: false })

/**
 * Formatted currency input backed by vue-currency-input.
 * Displays a locale-formatted value with the currency symbol inside the input (cs-CZ),
 * e.g. "4 500 Kč" or "5 200 €". Emits a plain Number via v-model.
 */
const props = defineProps({
  /** The numeric model value bound via v-model; String is accepted for backward compatibility
   *  because the data model may store amounts as strings (legacy of vue-slider string keys) */
  modelValue: { type: [Number, String], default: null },
  /** ISO 4217 currency code (CZK, EUR, …) — controls formatting */
  currency: { type: String, default: 'CZK' },
  /** Minimum allowed value; when exceeded the plugin clamps the input on blur */
  min: { type: Number, default: undefined },
  /** Maximum allowed value; when exceeded the plugin clamps the input on blur */
  max: { type: Number, default: undefined },
})

/**
 * Builds a plain options object for vue-currency-input.
 * Must return a plain object — passing a Vue Ref/computed to useCurrencyInput spreads
 * the Ref's internal properties instead of the option values, breaking CurrencyFormat init.
 */
const buildOptions = () => ({
  currency: props.currency,
  locale: 'cs-CZ',
  // Show the currency symbol inside the input (e.g. "Kč", "€") rather than a separate suffix
  currencyDisplay: 'symbol',
  valueRange: {
    min: props.min,
    max: props.max,
  },
})

// Initialize with a plain object snapshot — NOT a reactive ref (see buildOptions comment)
const { inputRef, formattedValue, setOptions } = useCurrencyInput(buildOptions())

// When currency or limits change, push updated options via setOptions so the mask
// re-initializes correctly (e.g. switching CZK ↔ EUR changes decimal/grouping separators)
watch(
  () => [props.currency, props.min, props.max],
  () => setOptions(buildOptions())
)
</script>

<template>
  <!-- Currency symbol is shown inside the input by the plugin — no input-group wrapper needed -->
  <input ref="inputRef" :value="formattedValue" v-bind="$attrs" class="form-control" />
</template>